import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Form, Spinner } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import aiChatbotsService, { AIChatbot } from "../../features/ai_chatbots/aiChatbotsService";
import "./personal-chat-modern.css";

type ChatMessage = {
  role: "user" | "assistant";
  content: string;
};

const PersonalChatPage: React.FC = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const [chatbots, setChatbots] = useState<AIChatbot[]>([]);
  const [selectedChatbotId, setSelectedChatbotId] = useState<number | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [hasMoreHistory, setHasMoreHistory] = useState(false);
  const [historySkip, setHistorySkip] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const isPrependingHistoryRef = useRef(false);

  useEffect(() => {
    const fetchChatbots = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await aiChatbotsService.getChatbots({ skip: 0, limit: 100, is_active: true });
        const list: AIChatbot[] = response?.data || response?.chatbots || [];
        setChatbots(list);

        const fromRoute = Number((location.state as any)?.chatbotId || 0);
        const fromStorage = Number(localStorage.getItem("personal_selected_chatbot_id") || 0);
        const firstId = list[0]?.id || null;
        const preferredId = [fromRoute, fromStorage, firstId].find((id) => !!id) || null;
        setSelectedChatbotId(preferredId as number | null);
      } catch (err: any) {
        setError(err?.response?.data?.detail || t("personal_chatbots_load_failed"));
      } finally {
        setIsLoading(false);
      }
    };

    fetchChatbots();
  }, [location.state, t]);

  const selectedChatbot = useMemo(
    () => chatbots.find((c) => c.id === selectedChatbotId) || null,
    [chatbots, selectedChatbotId]
  );

  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    if (isPrependingHistoryRef.current) {
      isPrependingHistoryRef.current = false;
      return;
    }
    el.scrollTop = el.scrollHeight;
  }, [messages, isTyping]);

  useEffect(() => {
    const bodyOverflow = document.body.style.overflow;
    const htmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = bodyOverflow;
      document.documentElement.style.overflow = htmlOverflow;
    };
  }, []);

  useEffect(() => {
    const loadHistory = async () => {
      if (!selectedChatbotId) {
        setMessages([]);
        setHasMoreHistory(false);
        setHistorySkip(0);
        return;
      }
      try {
        setIsLoadingHistory(true);
        const history = await aiChatbotsService.getPersonalChatHistory(selectedChatbotId, {
          skip: 0,
          limit: 30,
        });
        const formatted = (history.messages || [])
          .filter((m) => m.role === "user" || m.role === "assistant")
          .map((m) => ({ role: m.role as "user" | "assistant", content: m.content || "" }));
        setMessages(formatted);
        setHasMoreHistory(!!history.has_more);
        setHistorySkip(formatted.length);
      } catch {
        setMessages([]);
        setHasMoreHistory(false);
        setHistorySkip(0);
      } finally {
        setIsLoadingHistory(false);
        setIsTyping(false);
      }
    };

    loadHistory();
  }, [selectedChatbotId]);

  const loadOlderMessages = async () => {
    const el = messagesContainerRef.current;
    if (!selectedChatbotId || !hasMoreHistory || isLoadingHistory || !el) return;
    try {
      setIsLoadingHistory(true);
      const prevHeight = el.scrollHeight;
      const prevTop = el.scrollTop;
      const history = await aiChatbotsService.getPersonalChatHistory(selectedChatbotId, {
        skip: historySkip,
        limit: 30,
      });
      const older = (history.messages || [])
        .filter((m) => m.role === "user" || m.role === "assistant")
        .map((m) => ({ role: m.role as "user" | "assistant", content: m.content || "" }));
      if (older.length > 0) {
        isPrependingHistoryRef.current = true;
        setMessages((prev) => [...older, ...prev]);
        setHistorySkip((prev) => prev + older.length);
        setHasMoreHistory(!!history.has_more);
        requestAnimationFrame(() => {
          const node = messagesContainerRef.current;
          if (!node) return;
          node.scrollTop = node.scrollHeight - prevHeight + prevTop;
        });
      } else {
        setHasMoreHistory(false);
      }
    } catch {
      // no-op
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleMessagesScroll = async () => {
    const el = messagesContainerRef.current;
    if (!el) return;
    if (el.scrollTop <= 40) {
      await loadOlderMessages();
    }
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || !selectedChatbotId || isSending) return;

    setIsSending(true);
    setIsTyping(true);
    setError(null);
    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setInput("");

    try {
      const response = await aiChatbotsService.sendPersonalChatMessage(selectedChatbotId, text);
      setMessages((prev) => [...prev, { role: "assistant", content: response.message || "" }]);
    } catch (err: any) {
      setError(err?.response?.data?.detail || t("personal_message_send_failed"));
    } finally {
      setIsTyping(false);
      setIsSending(false);
    }
  };

  return (
    <div className="pcx-shell pcx-chat-shell">
      <div className="pcx-chat-layout">
        <div className="pcx-chat-header">
          <div>
            <div className="pcx-chat-title">{t("chat")}</div>
          </div>
          <div className="pcx-chatbot-select-wrap">
            <Form.Select
              className="pcx-chatbot-select"
              value={selectedChatbotId || ""}
              onChange={(e) => {
                const chatbotId = Number(e.target.value);
                setSelectedChatbotId(chatbotId);
                localStorage.setItem("personal_selected_chatbot_id", String(chatbotId));
              }}
              disabled={isLoading || chatbots.length === 0}
            >
              {chatbots.map((chatbot) => (
                <option key={chatbot.id} value={chatbot.id}>
                  {chatbot.name}
                </option>
              ))}
            </Form.Select>
          </div>
        </div>

        {error && <Alert variant="danger">{error}</Alert>}

        <div className="pcx-chat-window">
          <div className="pcx-chat-window-head">
            <span className="pcx-window-bot-name">{selectedChatbot?.name || t("chat")}</span>
            <span className="pcx-window-status">{t("online")}</span>
          </div>

          <div ref={messagesContainerRef} className="pcx-messages-scroll" onScroll={handleMessagesScroll}>
            {isLoadingHistory && hasMoreHistory ? (
              <div className="pcx-history-loader">
                <Spinner animation="border" size="sm" />
              </div>
            ) : null}
            {isLoading ? (
              <div className="d-flex align-items-center gap-2">
                <Spinner animation="border" size="sm" />
                <span>{t("loading")}</span>
              </div>
            ) : null}

            {!isLoading && messages.length === 0 ? (
              <div className="pcx-empty-chat">{t("personal_chat_empty")}</div>
            ) : null}

            {messages.map((msg, index) => (
              <div
                key={`${msg.role}-${index}`}
                className={`pcx-msg-row ${msg.role === "user" ? "pcx-msg-user-row" : "pcx-msg-assistant-row"}`}
              >
                <div className={`pcx-msg-bubble ${msg.role === "user" ? "pcx-msg-user" : "pcx-msg-assistant"}`}>
                  {msg.content}
                </div>
              </div>
            ))}
            {isTyping ? (
              <div className="pcx-msg-row pcx-msg-assistant-row">
                <div className="pcx-msg-bubble pcx-msg-assistant pcx-typing-bubble">
                  <span className="pcx-typing-dots" aria-hidden="true">
                    <i></i><i></i><i></i>
                  </span>
                  <span>{t("typing")}</span>
                </div>
              </div>
            ) : null}
          </div>

          <Form onSubmit={handleSend} className="pcx-input-bar">
            <Form.Control
              className="pcx-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t("write_your_message")}
              disabled={!selectedChatbotId || isSending}
            />
            <button className="pcx-btn pcx-send-btn" type="submit" disabled={!selectedChatbotId || isSending}>
              {isSending ? <Spinner animation="border" size="sm" /> : t("send")}
            </button>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default PersonalChatPage;
